import React, { Fragment } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import queryString from "query-string";

import Table from "./table";
import FiltersWrapper from "../filters";
import Pagination from "./pagination";
import SearchInput from "../ui/SearchInput";
import FilterListIcon from "@material-ui/icons/FilterList";

import { black600, white } from "../../theme/colors";
import _debounce from "lodash.debounce";
import { TOGGLE_FILTER } from "../../helpers/constants/actions";
import IconWithRedBubble from "../ui/IconWithRedBubble";
import { withRouter } from "react-router-dom";
import appendToSearch from "../../helpers/utils/appendToSearch";
import ContentWrapper from "../styles/ContentWrapper";
import { baseCaption } from "../../theme/typography";
import Loader from "../Loader";
import { isMobileScreen } from "../../helpers/isMobileScreen";


const Wrapper = styled.div`
  width: 100%;
  overflow: visible;
  margin-top: 16px;
  margin-bottom: 24px;
`;

const Content = styled.div`
  margin: ${(props) => props.margin || "20px 0"};
  position: relative;
  width: 100%;
  max-width: 100%;
  max-height: ${(props) => (props.maxHeight ? props.maxHeight : "")};
  background: white;
  display: flex;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(91, 127, 220, 0.10);
  overflow: hidden;

  @media (max-width: 995px) {
    margin: ${(props) => props.margin || "14px 0"};
  }
`;

/* Left frozen panel: scrolled programmatically only, kept in sync with the right panel */
const FrozenLeftPanel = styled.div`
  flex-shrink: 0;
  overflow-y: hidden;
  overflow-x: hidden;
  border-right: 2px solid #e2e8f0;
  box-shadow: 4px 0 8px rgba(0,0,0,0.06);
  background: white;
  z-index: 2;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;       /* Firefox */
  -ms-overflow-style: none;    /* IE/Edge */
  &::-webkit-scrollbar {
    display: none;             /* Chrome/Safari/Opera */
  }
`;

const BlankScreen = styled(Content)`
  min-height: ${(props) =>
    props.count && !props.shouldRestrictHeight
      ? `${(props.count + 1) * 64}px`
      : `240px`};
  width: ${(props) => (props.width ? `${props.width}px` : "100%")};
  max-width: 100%;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background-color: ${white};
  padding: 40px 16px;
  box-sizing: border-box;

  p {
    margin: 0;
    color: #64748b;
    font-size: 15px;
    font-weight: 500;
  }
`;

const ShowNumberOfRecords = styled.p`
  ${baseCaption}
  color: ${black600};
`;

const Loading = () => <Loader position="relative" />;

/* ──────────────────────────────────────────
   Modern 3-breakpoint Responsive Table Header
   Desktop  ≥ 1024px : title left, actions right (single row)
   Tablet  768–1023px: title + right-actions row 1; left-actions row 2
   Mobile   < 768px  : fully stacked, search full-width
────────────────────────────────────────── */
const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  width: 100%;
  box-sizing: border-box;

  /* Tablet */
  @media (max-width: 1023px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  /* Mobile */
  @media (max-width: 767px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 16px;
  }
`;

const TableTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin-right: 12px;

  h3, & > div, & > span, p {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.4px;
    font-family: 'Inter', 'Barlow', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  /* Tablet */
  @media (max-width: 1023px) {
    width: auto;
    h3, & > div, & > span, p { font-size: 18px; }
  }

  /* Mobile */
  @media (max-width: 767px) {
    width: auto;
    h3, & > div, & > span, p { font-size: 16px; }
  }
`;

const TableActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  width: auto;

  /* Tablet */
  @media (max-width: 1023px) {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 10px;
    width: 100%;
  }

  /* Mobile */
  @media (max-width: 995px) {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 10px;
    width: 100%;
  }
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;

  /* Tablet & Mobile */
  @media (max-width: 1023px) {
    margin-left: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    justify-content: flex-start !important;
    gap: 10px !important;
    width: 100% !important;
  }
`;

const RightActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 1023px) {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    gap: 8px !important;
    width: 100% !important;

    & > * {
      flex: 1 1 auto !important;
      min-width: max-content !important;
      text-align: center !important;
    }

    button {
      min-height: 32px !important;
      height: 32px !important;
      padding: 0 10px !important;
      font-size: 12px !important;
      border-radius: 6px !important;

      span {
        font-size: 12px !important;
      }
      svg {
        width: 15px !important;
        height: 15px !important;
      }
    }
  }
`;

const SearchFilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 1023px) {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 8px !important;
    width: 100% !important;

    /* Search input takes full remaining space */
    & > div:first-of-type,
    & > div[class*="SearchWrapper"] {
      flex: 1 1 auto !important;
      min-width: 0 !important;
      max-width: 100% !important;
      width: 100% !important;
    }
  }
`;

const LeftActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  flex: 0 1 auto;
  min-width: 0;

  @media (max-width: 1023px) {
    gap: 8px;
    width: 100% !important;
    flex: 1 1 100%;
  }

  @media (max-width: 767px) {
    gap: 8px !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => props.showTotal ? 'space-between' : 'flex-end'};
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;

  /* Tablet */
  @media (max-width: 1023px) {
    justify-content: space-between;
  }

  /* Mobile */
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding-top: 12px;

    & > * {
      justify-content: center;
    }
  }
`;

class ReactTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      pageSize: 15,
      searchTerm: props.searchValue || "",
      hoveredRowIndex: null,
      scrollbarHeight: 0,
    };

    // Refs for synchronized vertical scrolling in two-panel frozen layout
    this.leftPanelRef = React.createRef();
    this.rightPanelRef = React.createRef();
  }

  componentDidMount = () => {
    this.handleSearchDebounced = _debounce(function () {
      this.props.searchOnChange(this.state.searchTerm);
    }, 100);

    const { location } = this.props;
    if (location.search) {
      const state = { ...this.state };

      const qs = queryString.parse(location.search);

      if (qs["page-size"]) {
        state.pageSize = parseInt(qs["page-size"], 10);
      }

      this.setState(state);
    }

    this.updateScrollbarHeight();
    window.addEventListener("resize", this.updateScrollbarHeight);
  };

  componentDidUpdate(prevProps, prevState) {
    this.updateScrollbarHeight();
  }

  updateScrollbarHeight = () => {
    if (this.rightPanelRef && this.rightPanelRef.current) {
      const el = this.rightPanelRef.current;
      const sbHeight = el.offsetHeight - el.clientHeight;
      if (this.state.scrollbarHeight !== sbHeight) {
        this.setState({ scrollbarHeight: sbHeight });
      }
    }
  };

  /**
   * Clear All filters
   *
   */
  clearFilters = () => {
    const { onClearAllFilter } = this.props;

    this.toggleFilterList();

    if (typeof onClearAllFilter === "function") {
      onClearAllFilter();
    }
  };

  // Two-panel frozen layout: the RIGHT panel is the single source of truth for
  // scroll position. The left panel never scrolls on its own (overflow hidden);
  // wheel input over it is forwarded to the right panel, and it mirrors the
  // right panel's scrollTop. This avoids the two panels drifting out of sync.
  syncFromRight = (e) => {
    const left = this.leftPanelRef.current;
    if (left && left.scrollTop !== e.target.scrollTop) {
      left.scrollTop = e.target.scrollTop;
    }
  };

  // Native (non-passive) listener so preventDefault can stop the page scrolling
  onLeftPanelWheel = (e) => {
    const right = this.rightPanelRef.current;
    if (!right) return;

    // Normalize line/page delta modes (Firefox) to pixels
    const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? right.clientHeight : 1;
    let deltaY = e.deltaY * factor;
    let deltaX = e.deltaX * factor;
    if (e.shiftKey && !deltaX) {
      deltaX = deltaY;
      deltaY = 0;
    }

    const prevTop = right.scrollTop;
    const prevLeft = right.scrollLeft;
    right.scrollTop = prevTop + deltaY;
    right.scrollLeft = prevLeft + deltaX;

    // Only swallow the event if the table actually scrolled; otherwise let the page scroll
    if (right.scrollTop !== prevTop || right.scrollLeft !== prevLeft) {
      e.preventDefault();
    }
  };

  setLeftPanelRef = (el) => {
    if (this.leftPanelRef.current) {
      this.leftPanelRef.current.removeEventListener("wheel", this.onLeftPanelWheel);
    }
    this.leftPanelRef.current = el;
    if (el) {
      el.addEventListener("wheel", this.onLeftPanelWheel, { passive: false });
      if (this.rightPanelRef.current) {
        el.scrollTop = this.rightPanelRef.current.scrollTop;
      }
    }
  };

  setHoveredRowIndex = (index) => {
    this.setState({ hoveredRowIndex: index });
  };

  /**
   * Action to handle Apply button
   *
   * @param {string} key
   * @param {*} value
   */
  applyFilter = (newDates) => {
    const { onApplyFitler } = this.props;
    this.toggleFilterList();

    if (typeof onApplyFitler === "function") {
      onApplyFitler(newDates);
    }
  };

  sanitizeFilters() {
    let payload = {};
    this.state.filters ||
      [].forEach((item) => {
        payload[item.key] = item.stateValue;
      });
    return payload;
  }

  nextPage() {
    this.props.onPageChange(
      this.props.nextPage,
      this.sanitizeFilters(),
      this.state.order
    );
  }

  previousPage() {
    this.props.onPageChange(
      this.props.previousPage,
      this.sanitizeFilters(),
      this.state.order
    );
  }

  onSearchChange = (e) => {
    const val = e.target.value;
    this.setState({ searchTerm: val }, () => {
      if (val === "") {
        this.props.searchOnChange("");
      }
    });
  };

  onSearchClick = (e) => {
    // No-op to prevent click-triggered API requests
  };

  handleKeyDown = (event) => {
    if (event.key === "Enter") {
      this.props.searchOnChange(this.state.searchTerm);
    }
  };

  onPageSizeChange = ({ value }) => {
    const { history } = this.props;

    this.setState({ pageSize: value }, () => {
      const { history, location } = this.props;
      history.push({
        route: "/",
        search: appendToSearch(location.search, {
          "page-size": value,
          page: 1,
        }),
        state: { ...history.location.state },
      });
    });
  };

  toggleFilterList = () => {
    const isShow = !this.state.showFilterList;
    this.setState({ showFilterList: isShow });
    if (typeof this.props.filterHandler === "function") {
      this.props.filterHandler();
    }
    this.props.showFilterAction(isShow);
  };

  componentWillUnmount() {
    this.props.showFilterAction(false);
    window.removeEventListener("resize", this.updateScrollbarHeight);
  }

  renderTable() {
    const {
      data,
      rowHeight,
      rowStyle,
      columns,
      loading,
      tableStyle,
      order,
      onRowClick,
      onOrderChange,
      fluidTable = false,
      rowActionType = "VIEW",
      rowActionElement,
      enableCheckbox = false,
      disableRowClick = false,
      rowActionElementWidth,
      tableRightBlockElement,
      maxHeight = "500px",
      selectedOutletRows,
      isCursor,
      isPosition,
      isBorder,
      useStatusColor,
      singleScrollTable = false,
    } = this.props;

    const width = columns.length * 175;
    const shouldRestrictHeight =
      false || columns.filter((column) => column.fixed).length === 0;

    const leftColumns = columns.filter((column) => column.fixed === "left") || [];
    const rightColumns = columns.filter((column) => column.fixed === "right") || [];
    const centerColumns = columns.filter((column) => !column.fixed) || [];

    // For non-frozen tables, use all columns together (original behavior)
    const nonFrozenColumns = columns;

    // Compute total width of left frozen columns for left panel sizing
    const leftColumnsWidth = leftColumns.reduce(
      (sum, col) => sum + (col.width ? parseInt(col.width, 10) : 175),
      0
    );
    const leftWidth = leftColumnsWidth + 24;

    if (loading) {
      return (
        <BlankScreen
          style={tableStyle}
          margin="24px 0"
          shouldRestrictHeight={shouldRestrictHeight}
        >
          <Loading />
        </BlankScreen>
      );
    }

    if (!data || data.length === 0) {
      return (
        <BlankScreen
          style={tableStyle}
          margin="24px 0"
          shouldRestrictHeight={shouldRestrictHeight}
        >
          <p>No Data Available</p>
        </BlankScreen>
      );
    }

    const isMobile = isMobileScreen();
    if (isMobile) {
      return (
        <Table
          updateOrder={onOrderChange}
          order={order}
          columns={columns}
          data={data}
          rowStyle={rowStyle}
          onRowClick={onRowClick}
          rowActionType={rowActionType}
          rowActionElement={rowActionElement}
          rowActionElementWidth={rowActionElementWidth}
          enableCheckbox={enableCheckbox}
          fluidTable={true}
          rowHeight={rowHeight}
          disableRowClick={disableRowClick}
          selectedOutletRows={selectedOutletRows}
          isCursor={isCursor}
          isPosition={isPosition}
          useStatusColor={useStatusColor}
          applyFilter={this.applyFilter}
          isBorder={isBorder}
        />
      );
    }

    // --- Two-panel layout: left frozen panel + right scrollable panel ---
    if (singleScrollTable && leftColumns.length > 0) {
      return (
        <Content
          style={tableStyle}
          margin="0"
          shouldRestrictHeight={shouldRestrictHeight}
          maxHeight={maxHeight}
          frozenLayout={true}
        >
          {/* Left frozen panel - mirrors the right panel's scroll; wheel input is forwarded to it */}
          <FrozenLeftPanel
            ref={this.setLeftPanelRef}
            style={{ width: `${leftWidth}px` }}
          >
            <Table
              updateOrder={onOrderChange}
              order={order}
              columns={leftColumns}
              position="left"
              data={data}
              rowStyle={rowStyle}
              useStatusColor={useStatusColor}
              onRowClick={onRowClick}
              rowHeight={rowHeight}
              disableRowClick={disableRowClick}
              isCursor={isCursor}
              isPosition={isPosition}
              applyFilter={this.applyFilter}
              isBorder={isBorder}
              selectedOutletRows={selectedOutletRows}
              hoveredRowIndex={this.state.hoveredRowIndex}
              onRowHover={this.setHoveredRowIndex}
            />
            {this.state.scrollbarHeight > 0 && (
              <div
                style={{
                  height: `${this.state.scrollbarHeight}px`,
                  minHeight: `${this.state.scrollbarHeight}px`,
                  flexShrink: 0,
                  background: 'white',
                }}
              />
            )}
          </FrozenLeftPanel>
          {/* Right scrollable panel */}
          <div
            ref={this.rightPanelRef}
            onScroll={this.syncFromRight}
            style={{
              flex: 1,
              overflow: 'auto',
              minWidth: 0,
              background: 'white',
            }}
          >
            <Table
              updateOrder={onOrderChange}
              order={order}
              columns={centerColumns}
              data={data}
              rowStyle={rowStyle}
              onRowClick={onRowClick}
              rowActionType={rowActionType}
              rowActionElement={rowActionElement}
              rowActionElementWidth={rowActionElementWidth}
              enableCheckbox={enableCheckbox}
              fluidTable={false}
              rowHeight={rowHeight}
              disableRowClick={disableRowClick}
              selectedOutletRows={selectedOutletRows}
              isCursor={isCursor}
              isPosition={isPosition}
              useStatusColor={useStatusColor}
              applyFilter={this.applyFilter}
              isBorder={isBorder}
              rightPanel={true}
              hoveredRowIndex={this.state.hoveredRowIndex}
              onRowHover={this.setHoveredRowIndex}
            />
          </div>
          {tableRightBlockElement}
        </Content>
      );
    }

    // --- Default layout: original behavior, all columns in one table ---
    return (
      <Content
        style={tableStyle}
        margin="0"
        shouldRestrictHeight={shouldRestrictHeight}
        maxHeight={maxHeight}
      >
        <Table
          updateOrder={onOrderChange}
          order={order}
          columns={nonFrozenColumns}
          data={data}
          rowStyle={rowStyle}
          onRowClick={onRowClick}
          rowActionType={rowActionType}
          rowActionElement={rowActionElement}
          rowActionElementWidth={rowActionElementWidth}
          enableCheckbox={enableCheckbox}
          fluidTable={nonFrozenColumns.length < 4 || fluidTable}
          rowHeight={rowHeight}
          disableRowClick={disableRowClick}
          selectedOutletRows={selectedOutletRows}
          isCursor={isCursor}
          isPosition={isPosition}
          useStatusColor={useStatusColor}
          applyFilter={this.applyFilter}
          isBorder={isBorder}
          hoveredRowIndex={this.state.hoveredRowIndex}
          onRowHover={this.setHoveredRowIndex}
        />
        {tableRightBlockElement}
      </Content>
    );
  }

  render() {
    const {
      current,
      count,
      className,
      total,
      totalPages,
      previousPage,
      nextPage,
      showPagination = true,
      title = "",
      showSearch = false,
      showFilter = false,
      showFilterLoading = false,
      leftHeaderElements = null,
      rightHeaderElements = null,
      filterList = [],
      data,
      filtersApplied,
      showTotalRecordText = false,
      rightHeaderElementsBeforeIcons,
      tableRightBlockElement,
      additionalFilters,
      pagination,
    } = this.props;
    const { searchTerm, showFilterList, pageSize } = this.state;

    /* ── Right side: search + filter + extra actions ── */
    const searchFilterElement = (showSearch || showFilter) ? (
      <SearchFilterGroup className="table-header-search-group">
        {showSearch ? (
          <SearchInput
            value={searchTerm}
            onChange={this.onSearchChange}
            onClick={this.onSearchClick}
            onEnter={this.handleKeyDown}
          />
        ) : null}
        {showFilter ? (
          <IconWithRedBubble
            applied={!showFilterList && filtersApplied}
            onClick={this.toggleFilterList}
          >
            <FilterListIcon />
          </IconWithRedBubble>
        ) : null}
      </SearchFilterGroup>
    ) : null;

    const rightActions = (
      <TableActions>
        {rightHeaderElementsBeforeIcons && rightHeaderElements ? (
          <RightActionButtons className="table-header-right-buttons">
            {rightHeaderElements}
          </RightActionButtons>
        ) : null}
        {searchFilterElement}
        {!rightHeaderElementsBeforeIcons && rightHeaderElements ? (
          <RightActionButtons className="table-header-right-buttons">
            {rightHeaderElements}
          </RightActionButtons>
        ) : null}
      </TableActions>
    );

    return (
      <Wrapper className={className}>
        {/* ── Modern Responsive Header ── */}
        <TableHeader>
          {/* Title */}
          {title ? (
            <TableTitleRow>
              {typeof title === "string" ? <h3>{title}</h3> : title}
            </TableTitleRow>
          ) : null}

          {/* Actions row: buttons on left, search/filter on right */}
          {leftHeaderElements || showSearch || showFilter || rightHeaderElements ? (
            <TableActionRow hasLeftActions={Boolean(leftHeaderElements)}>
              {leftHeaderElements ? (
                <LeftActions>
                  {leftHeaderElements}
                </LeftActions>
              ) : null}
              {rightActions}
            </TableActionRow>
          ) : null}
        </TableHeader>

        {this.renderTable()}

        {showPagination && (data || []).length ? (
          <PaginationWrapper showTotal={showTotalRecordText}>
            {showTotalRecordText ? (
              <ShowNumberOfRecords>
                Showing {current === 1 ? 1 : pageSize * previousPage + 1} –{" "}
                {Math.ceil(pageSize * current) > total
                  ? total
                  : Math.ceil(pageSize * current)}{" "}
                of {total} {total > 1 ? "Records" : "Record"}
              </ShowNumberOfRecords>
            ) : null}
            <Pagination
              current={current}
              count={(data || []).length}
              totalPages={totalPages || Math.ceil(total / pageSize)}
              onNextPageClick={nextPage ? () => this.nextPage() : null}
              onPrevPageClick={
                previousPage ? () => this.previousPage() : null
              }
              pageSize={pageSize}
              onPageSizeChange={this.onPageSizeChange}
            />
          </PaginationWrapper>
        ) : null}

        <FiltersWrapper
          showLoading={showFilterLoading}
          open={showFilterList}
          closeAction={this.toggleFilterList}
          filterList={filterList}
          onClearAllAction={this.clearFilters}
          onApplyFilterAction={(dates) => {
            this.applyFilter(dates);
          }}
          additionalFilters={additionalFilters}
        />
      </Wrapper>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    showFilterAction: (value = false) => {
      dispatch({
        type: TOGGLE_FILTER,
        payload: {
          isFilterOpen: value,
        },
      });
    },
  };
};

export default withRouter(connect(null, mapDispatchToProps)(ReactTable));
